import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "../../../hooks/useAuth";
import {
  getConversations,
  getOmniConversations,
  getConversation,
  getAILogs,
  getAILog,
  isEndpointMissing,
  channelMatchesFilter,
  connectHelpdeskStream,
  connectOmniHelpdeskStream,
} from "../../../services/aiChatService";
import {
  getFolders,
  createFolder,
  updateFolder,
  deleteFolder,
  setConversationFolder,
} from "../../../services/helpdeskFoldersService";
import {
  accountKeyOf,
  CHANNEL_ACCOUNT_KINDS,
} from "../../../layouts/helpdeskLayout/HelpdeskRail";
import {
  cachedChannelAccounts,
  fetchChannelAccounts,
} from "../../../api/helpdeskChannelAccounts";
import { parseTicketQuery } from "../../../components/helpdesk/tickets";
import { apiErrorMessage } from "../../../utils/apiErrorMessage";
import { toast } from "../../../utils/toast";
import {
  getPins,
  pinConversation,
  unpinConversation,
  isPinLimitError,
  PIN_LIMIT,
} from "../../../services/helpdeskPinsService";
import {
  computeInboxCounters,
  filterVisibleConversations,
  inboxKeyToBaseFilters,
  inboxKeyToClientFilter,
  mapAILogToConv,
  normalizePins,
  searchResultConversation,
  sortPinnedFirst,
  withPinnedExtras,
  isPinnable,
} from "./inboxModel";
import { useUserNames } from "./useUserNames";
import { useInboxServerSearch, SEARCH_MIN_CHARS } from "./useInboxServerSearch";
import { useDesktopNotifications } from "./useDesktopNotifications";

// `agenda` (lo que la agenda del operador dice del contacto) solo la trae
// GET /conversations/:id; los eventos del SSE y las respuestas de las
// mutaciones (asignar, resolver, mover de carpeta) traen el documento sin ella.
// La vista de atención clasifica la audiencia con detail.agenda: si el payload
// reemplazara la selección tal cual, un repartidor asignado a mano en la agenda
// pasaría a verse como 'destinatario' hasta reabrir el hilo. Por eso, cuando el
// payload no la trae, se conserva la de la MISMA conversación (se refresca con
// la próxima lectura del detalle). Un `agenda: null` explícito sí se respeta.
function keepDetailAgenda(previous, updated) {
  if (updated.agenda !== undefined) return updated;
  if (previous?.id !== updated.id || previous.agenda === undefined) return updated;
  return { ...updated, agenda: previous.agenda };
}

/**
 * Datos de la bandeja de la mesa de ayuda: filtros, lista (unión de fuentes),
 * detalle con guarda anti-carreras, carpetas, cuentas, búsqueda server-side,
 * fijados por usuario y tiempo real (SSE + reconciliación).
 *
 * UN consumidor por pantalla: cada instancia abre sus streams SSE.
 *
 * No sabe de layout (pantallas de móvil, anchos, modo foco): la página compone
 * esas reacciones alrededor de las funciones que devuelve. El objeto devuelto
 * se recrea en cada render (las funciones son estables): hay que desestructurar.
 *
 * @param {object} [options]
 * @param {string|(() => string)} [options.initialInboxKey="inbox.unassigned"]
 *   Bandeja inicial (valor o inicializador perezoso de useState).
 * @param {boolean} [options.initialShowAllNumbers=false]
 *   true = arranca incluyendo los números dedicados solo a notificaciones
 *   (include_all). Es estado local de la instancia: no se persiste ni se
 *   comparte con otras pantallas.
 * @param {boolean} [options.legacySources=true]
 *   false = solo la fuente omnicanal: sin conversaciones del portal, sin logs
 *   de IA y sin el stream /helpdesk/stream (las tres pasan por el bridge de IA).
 *   Es fija por instancia: el stream se abre con el valor del montaje.
 * @param {boolean} [options.activeOnlyServerSide=false]
 *   true = "Todas" (inbox.all) le pide a omnicanal status 'active' (todo menos
 *   resuelto, filtrado en el servidor). Sin esto, las resueltas viajan dentro
 *   del tope de 1000 y el cliente las oculta: con include_all, las abiertas
 *   más antiguas pueden quedar fuera de la lista.
 */
export function useHelpdeskInbox({
  initialInboxKey = "inbox.unassigned",
  initialShowAllNumbers = false,
  legacySources = true,
  activeOnlyServerSide = false,
} = {}) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [inboxKey, setInboxKey] = useState(initialInboxKey);

  // Filtros base de la bandeja: los usa el fetch y también la inclusión de
  // fijadas traídas aparte (que no pasaron por el fetch).
  const baseFilters = useMemo(() => inboxKeyToBaseFilters(inboxKey), [inboxKey]);
  const { statusFilter, channelFilter, folderId, aiState, retiredAccountId } = baseFilters;
  // "Todas" con las resueltas filtradas en el servidor. Va como primitivo
  // aparte porque "Todas", "Vencidos" y "Grupos" comparten filtros base: sin él
  // en las deps de fetchList, pasar de una a otra no volvería a pedir.
  const omniActiveOnly = activeOnlyServerSide && inboxKey === "inbox.all";
  // Ref para el SSE (sus handlers se suscriben una vez): en la vista de un
  // número retirado no se insertan conversaciones nuevas de otros números.
  const retiredViewRef = useRef(!!retiredAccountId);
  useEffect(() => {
    retiredViewRef.current = !!retiredAccountId;
  }, [retiredAccountId]);
  const clientFilter = useMemo(
    () => inboxKeyToClientFilter(inboxKey, user?.id),
    [inboxKey, user?.id]
  );

  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(false);
  // La fuente de logs de IA (bridge legado) cayó en la unión de "Todas": la
  // bandeja puede quedar vacía o corta pero NO porque no haya atenciones. Sin
  // este flag el vacío genérico afirmaría "no hay conversaciones". "Atendidas
  // por IA" ya no lee esa fuente (ai_state=handled), así que no le aplica.
  const [aiLogsError, setAiLogsError] = useState(false);
  const [commerceFilter, setCommerceFilter] = useState(null);
  // Filtro por etiqueta (id o null): viaja como label_id a omnicanal y además
  // se aplica en el cliente (ver visibleConversations).
  const [labelFilter, setLabelFilter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [selectedConv, setSelectedConv] = useState(null);
  const [detailMessages, setDetailMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // Fallo al cargar el detalle: se muestra en el chat con reintento, en vez de
  // dejar el hilo vacío como si la conversación no tuviera mensajes.
  const [detailError, setDetailError] = useState(null);
  // Guardia contra respuestas fuera de orden: seleccionar A y enseguida B deja
  // el fetch de A en vuelo, y sin esto su error pisaba el detalle de B.
  const detailRequestRef = useRef(0);

  const [newIds, setNewIds] = useState(new Set());
  // Por defecto el inbox oculta los números dedicados solo a notificaciones;
  // la vista que los atiende arranca con ellos incluidos.
  const [showAllNumbers, setShowAllNumbers] = useState(initialShowAllNumbers);

  // Cuentas/números vinculados por canal + filtro por cuenta (sub-árbol del rail).
  const [channelAccounts, setChannelAccounts] = useState(
    () => cachedChannelAccounts() ?? []
  );
  // true cuando `channelAccounts` refleja una respuesta real (no el [] inicial
  // mientras carga): recién ahí se puede afirmar que un canal no está vinculado.
  const [channelAccountsLoaded, setChannelAccountsLoaded] = useState(
    () => cachedChannelAccounts() !== null
  );
  const [accountFilter, setAccountFilter] = useState(null);

  const { userNames, getDisplayName } = useUserNames(conversations, detailMessages);

  const sseCleanupRef = useRef(null);
  // Debounce del refetch de counts de carpetas ante ráfagas de status_change
  // del SSE (resolver/reabrir en lote): un solo GET al final de la ráfaga.
  const folderRefetchTimerRef = useRef(null);
  const selectedConvRef = useRef(selectedConv);
  useEffect(() => {
    selectedConvRef.current = selectedConv;
  }, [selectedConv]);
  const channelFilterRef = useRef(channelFilter);
  useEffect(() => {
    channelFilterRef.current = channelFilter;
  }, [channelFilter]);
  // Las respuestas en números de solo notificaciones se aplican en vivo solo
  // mientras la bandeja incluye esos números.
  const showAllNumbersRef = useRef(showAllNumbers);
  useEffect(() => {
    showAllNumbersRef.current = showAllNumbers;
  }, [showAllNumbers]);

  // ── Cargar lista ──────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    if (!user) return;
    setListLoading(true);
    setListError(false);
    // Cada corrida reevalúa la fuente de IA: un fallo transitorio no se latchea.
    setAiLogsError(false);
    try {
      const statusParam = statusFilter || undefined;
      // 'active' solo lo entiende omnicanal: el portal y los logs de IA siguen
      // pidiéndose con el estado de la bandeja.
      const omniStatusParam = omniActiveOnly ? "active" : statusParam;

      if (retiredAccountId) {
        // Vista de un número retirado: solo sus conversaciones abiertas (las
        // cerradas están en "Resueltos"). Sin comercio ni "ver todas": el
        // filtro ES el número.
        const result = await getOmniConversations({
          retired_account_id: retiredAccountId,
          status: "active",
        });
        setConversations(result.conversations ?? []);
      } else if (folderId) {
        // Vista de carpeta: SOLO la fuente omnicanal — las conversaciones de
        // portal y los logs de IA no tienen folder_id, no hay nada que unir.
        // status 'active' filtra las resueltas en el SERVIDOR: hacerlo en el
        // cliente sobre la página de 50 dejaba la lista corta (activas viejas
        // fuera de las 50 traídas) mientras el badge del rail — que excluye
        // resueltas sin límite — mostraba el total real.
        const result = await getOmniConversations({
          folder_id: folderId,
          status: "active",
          effective_commerce_id: commerceFilter?.id || undefined,
          label_id: labelFilter || undefined,
          include_all: showAllNumbers ? "true" : undefined,
        });
        setConversations(result.conversations ?? []);
      } else if (aiState) {
        // "IA en curso" (active) y "Atendidas por IA" (handled): solo fuente
        // omnicanal, que es donde escriben los agentes IA actuales. Con
        // `active` el servidor excluye resueltas; con `handled` las incluye.
        const result = await getOmniConversations({
          ai_state: aiState,
          effective_commerce_id: commerceFilter?.id || undefined,
          label_id: labelFilter || undefined,
          include_all: showAllNumbers ? "true" : undefined,
        });
        setConversations(result.conversations ?? []);
      } else if (channelFilter === "portal") {
        // Sin fuentes legacy no se le pide nada al bridge: la vista que las
        // apaga no ofrece esta bandeja y, si se llegara a ella, queda vacía.
        const result = legacySources
          ? await getConversations({ status: statusParam }, user)
          : { conversations: [] };
        setConversations(result.conversations ?? []);
      } else if (
        channelFilter === "whatsapp" ||
        channelFilter === "whatsapp_unofficial" ||
        channelFilter === "messenger" ||
        channelFilter === "instagram" ||
        // Canal interno: vive en el mismo microservicio omnicanal, así que se
        // pide con el mismo `channel` y no necesita una rama propia.
        channelFilter === "internal"
      ) {
        // TODO(back): cuando el endpoint acepte filtro por cuenta, sumar aquí
        // `channel_account: accountFilter || undefined` y quitar el filtrado
        // cliente-side de visibleConversations (hoy un param desconocido puede
        // hacer 400 en la validación del microservicio).
        const result = await getOmniConversations({
          channel: channelFilter,
          status: statusParam,
          effective_commerce_id: commerceFilter?.id || undefined,
          label_id: labelFilter || undefined,
          include_all: showAllNumbers ? "true" : undefined,
        });
        setConversations(result.conversations ?? []);
      } else if (!legacySources) {
        // Sin filtro de canal y sin fuentes legacy: no hay unión, omnicanal es
        // la única fuente. Si falla, la bandeja muestra el error en vez de un
        // vacío que afirmaría "no hay conversaciones".
        const result = await getOmniConversations({
          status: omniStatusParam,
          effective_commerce_id: commerceFilter?.id || undefined,
          label_id: labelFilter || undefined,
          include_all: showAllNumbers ? "true" : undefined,
        });
        setConversations(result.conversations ?? []);
      } else {
        // Sin filtro de canal ("Todas", "Sin asignar", "Mis casos", "Vencidos
        // (SLA)", "Grupos"): la bandeja es la UNIÓN de las tres fuentes.
        //
        // Esta rama era INALCANZABLE: la condición del portal era
        // `!channelFilter || channelFilter === "portal"`, así que el caso sin
        // canal se iba por el fetch del portal y la bandeja mostraba solo
        // conversaciones de portal — las de WhatsApp/Messenger/Instagram no
        // aparecían nunca, aunque el tab del canal sí las listaba.
        //
        // allSettled y no all: /helpdesk/conversations responde 403 hoy, y con
        // Promise.all ese rechazo vaciaba la bandeja entera. Cada fuente degrada
        // por separado; solo si caen portal Y omnicanal se propaga el error.
        const [portalRes, omniRes, aiRes] = await Promise.allSettled([
          getConversations({ status: statusParam }, user),
          getOmniConversations({
            status: omniStatusParam,
            effective_commerce_id: commerceFilter?.id || undefined,
            label_id: labelFilter || undefined,
            include_all: showAllNumbers ? "true" : undefined,
          }),
          // Los logs de IA no tienen estado de triage (mapAILogToConv los fija
          // en status:"ai"), así que solo entran cuando la sub-vista no filtra
          // por estado; si no, contaminarían "Sin asignar" / "Mis casos".
          statusParam ? Promise.resolve({ logs: [], count: 0 }) : getAILogs(),
        ]);

        if (portalRes.status === "rejected" && omniRes.status === "rejected") {
          throw portalRes.reason ?? omniRes.reason;
        }
        [portalRes, omniRes, aiRes]
          .filter((r) => r.status === "rejected")
          .forEach((r) => console.warn("Fuente del inbox no disponible:", r.reason));

        // Las atenciones por IA faltan en la unión: sin este aviso, una bandeja
        // que quedara vacía (o corta) se leería como "no hay nada", cuando en
        // realidad falta una de las tres fuentes.
        if (aiRes.status === "rejected") setAiLogsError(true);

        const settled = (r, fallback) =>
          r.status === "fulfilled" && r.value ? r.value : fallback;
        const portalResult = settled(portalRes, { conversations: [], count: 0 });
        const omniResult = settled(omniRes, { conversations: [], count: 0 });
        const aiResult = settled(aiRes, { logs: [], count: 0 });

        const aiMapped = (aiResult.logs ?? []).map(mapAILogToConv);
        const merged = [
          ...(portalResult.conversations ?? []),
          ...(omniResult.conversations ?? []),
          ...aiMapped,
        ].sort((a, b) => {
          const dateA = new Date(a.last_message_at || a.created_at || 0).getTime();
          const dateB = new Date(b.last_message_at || b.created_at || 0).getTime();
          return dateB - dateA;
        });
        setConversations(merged);
      }
    } catch (err) {
      console.error("Error al cargar conversaciones:", err);
      setConversations([]);
      setListError(true);
    } finally {
      setListLoading(false);
    }
  }, [user, statusFilter, channelFilter, folderId, aiState, retiredAccountId, commerceFilter, labelFilter, showAllNumbers, legacySources, omniActiveOnly]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // ── Carpetas del helpdesk ─────────────────────────────────────────────────
  // La lista (con counts) viene del backend. En error se conserva lo previo:
  // un fallo transitorio no debe vaciar el rail que el agente ya estaba viendo.
  const [folders, setFolders] = useState([]);
  // Distingue "aún no cargaron" de "cargaron y esta carpeta no existe": el
  // efecto que expulsa de una carpeta borrada NO puede correr antes del primer
  // fetch o sacaría al agente de una carpeta perfectamente válida.
  const [foldersLoaded, setFoldersLoaded] = useState(false);

  const fetchFolders = useCallback(async () => {
    if (!user) return;
    try {
      const list = await getFolders();
      setFolders(Array.isArray(list) ? list : []);
      setFoldersLoaded(true);
    } catch (err) {
      console.warn("Carpetas no disponibles:", err);
    }
  }, [user]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Ref para refetchear desde los handlers del SSE sin re-suscribir el stream
  // (mismo patrón que fetchListRef).
  const fetchFoldersRef = useRef(fetchFolders);
  useEffect(() => {
    fetchFoldersRef.current = fetchFolders;
  }, [fetchFolders]);

  // Índice id → carpeta. Referencia ESTABLE (useMemo sobre folders): baja como
  // prop a componentes memoizados y no puede regenerarse por render.
  const foldersById = useMemo(
    () => Object.fromEntries(folders.map((f) => [f.id, f])),
    [folders]
  );

  // Carpeta activa (primitivo derivado del inboxKey, para props y supresión
  // del chip en la lista).
  const activeFolderId = inboxKey.startsWith("folder.") ? inboxKey.slice(7) : null;

  // Un canal sin cuenta vinculada no está en el rail: si se llegó a él por un
  // enlace (?phone=, o el railKey de otra ruta), la vista vuelve a la bandeja
  // general en vez de filtrar por un ítem que no se ve.
  useEffect(() => {
    if (!channelAccountsLoaded || !Array.isArray(channelAccounts)) return;
    const kinds = CHANNEL_ACCOUNT_KINDS[inboxKey];
    if (!kinds || inboxKey === "channel.portal") return;
    if (!channelAccounts.some((account) => kinds.includes(account?.channel))) {
      setInboxKey("inbox.all");
    }
  }, [channelAccounts, channelAccountsLoaded, inboxKey]);

  // Si la carpeta activa desapareció (otro supervisor la borró, o la borré yo
  // desde el rail), la vista vuelve a la bandeja general.
  useEffect(() => {
    if (!activeFolderId || !foldersLoaded) return;
    if (!folders.some((f) => f.id === activeFolderId)) {
      setInboxKey("inbox.all");
    }
  }, [activeFolderId, folders, foldersLoaded]);

  // ── Conversaciones fijadas (pin por usuario) ─────────────────────────────
  // El estado es optimista sobre un Map<id, pinnedAtMs>: la fila se mueve al
  // instante y el servidor confirma (o se revierte) después, como WhatsApp Web.
  const [pins, setPins] = useState(() => new Map());
  // Fuente SÍNCRONA del estado: dos clics seguidos sobre la misma fila no
  // pueden leer un `pins` que todavía no pasó por el render (el segundo
  // calcularía el toggle sobre el estado viejo y desharía el primero).
  const pinsRef = useRef(pins);
  const commitPins = useCallback((next) => {
    pinsRef.current = next;
    setPins(next);
  }, []);
  // true recién tras un GET exitoso: mientras no lo esté, la UI no ofrece
  // fijar (endpoint no desplegado en este entorno, o todavía cargando).
  const [pinsAvailable, setPinsAvailable] = useState(false);
  // Ids con PATCH en vuelo: un segundo toggle sobre el mismo chat se
  // ignora mientras el primero no termine.
  const pinInFlightRef = useRef(new Set());
  // Se incrementa en cada togglePin. Un GET que arrancó antes del toggle y
  // responde después trae una foto vieja y no puede pisar el estado optimista.
  const pinsVersionRef = useRef(0);
  // Fijadas que no vinieron en la página cargada (se trajeron por el detalle).
  const [pinnedExtras, setPinnedExtras] = useState(() => new Map());
  // Ids ya pedidos: evita volver a pedir el detalle en cada render. Se vacía en
  // cada lectura de fijados (montaje, reconexión, foco de pestaña), así las
  // extras se reconcilian igual que la lista y un fallo puntual se reintenta.
  // Solo cuesta algo sin overlay del listado (ver el efecto de extras).
  const extrasRequestedRef = useRef(new Set());

  const fetchPins = useCallback(
    async () => {
      if (!user) return;
      const version = pinsVersionRef.current;
      try {
        const payload = await getPins();
        // Respuesta obsoleta: hubo un toggle entre el envío y la respuesta (o
        // hay uno en vuelo); el estado optimista manda.
        if (version !== pinsVersionRef.current || pinInFlightRef.current.size > 0) {
          return;
        }
        extrasRequestedRef.current = new Set();
        commitPins(normalizePins(payload));
        setPinsAvailable(true);
      } catch (err) {
        // Endpoint no desplegado (404/405) o rol fuera del equipo del helpdesk
        // (403): silencio y la UI no ofrece fijar.
        if (isEndpointMissing(err) || err?.status === 403) {
          setPinsAvailable(false);
          commitPins(new Map());
          return;
        }
        console.warn("Fijados no disponibles:", err);
        // Un fallo transitorio conserva lo previo: no despinea la pantalla.
      }
    },
    [user, commitPins]
  );

  useEffect(() => {
    fetchPins();
  }, [fetchPins]);

  // Ref para refetchear desde los handlers del SSE sin re-suscribir el stream
  // (los pins son por usuario: no hay evento propio de pins).
  const fetchPinsRef = useRef(fetchPins);
  useEffect(() => {
    fetchPinsRef.current = fetchPins;
  }, [fetchPins]);

  // Respaldo para una fijada que no vino en la página cargada: se trae por el
  // detalle para poder mostrarla arriba. El listado omnicanal actual ya
  // devuelve primero las fijadas que calzan con el filtro (y marca cada fila
  // con `pinned`), así que si la página trae ese campo no hay nada que pedir:
  // la fijada ausente es una que esta bandeja no debe mostrar. Con la lista
  // cargando tampoco: `conversations` aún no es la página de esta bandeja.
  const listOverlaysPins = useMemo(
    () => conversations.some((c) => typeof c?.pinned === "boolean"),
    [conversations]
  );
  useEffect(() => {
    if (!user || !pinsAvailable || pins.size === 0) return;
    if (listLoading || listError || listOverlaysPins) return;
    const loaded = new Set(conversations.map((c) => c.id));
    const missing = [...pins.keys()].filter(
      (id) => !loaded.has(id) && !extrasRequestedRef.current.has(id)
    );
    if (missing.length === 0) return;
    missing.forEach((id) => extrasRequestedRef.current.add(id));
    // "whatsapp" solo decide el enrutamiento (portal vs omnicanal): las
    // fijadas son siempre omnicanal, y el detalle trae el canal verdadero.
    Promise.allSettled(missing.map((id) => getConversation(id, user, "whatsapp"))).then(
      (results) => {
        const fetched = [];
        results.forEach((r, i) => {
          if (r.status === "fulfilled" && r.value?.id) {
            // El detalle trae el hilo: no se guarda en la fila de la lista.
            const { messages, ...row } = r.value;
            fetched.push([missing[i], row]);
          }
        });
        if (fetched.length === 0) return;
        setPinnedExtras((prev) => {
          const next = new Map(prev);
          fetched.forEach(([id, row]) => next.set(id, row));
          return next;
        });
      }
    );
  }, [user, pinsAvailable, pins, conversations, listLoading, listError, listOverlaysPins]);

  const togglePin = useCallback(
    async (conv) => {
      if (!isPinnable(conv)) return;
      const id = conv.id;
      if (pinInFlightRef.current.has(id)) return;
      const current = pinsRef.current;
      const wasPinned = current.has(id);
      if (!wasPinned && current.size >= PIN_LIMIT) {
        // Tope local: se avisa sin gastar la petición (el backend lo aplica
        // igual, pero su 409 es solo la red de seguridad).
        toast.warning(t("Puedes fijar hasta 5 chats"));
        return;
      }
      const previousAt = current.get(id);
      pinInFlightRef.current.add(id);
      pinsVersionRef.current += 1;
      // Optimista: la fila se mueve antes de que el servidor conteste.
      const optimistic = new Map(current);
      if (wasPinned) optimistic.delete(id);
      else optimistic.set(id, Date.now());
      commitPins(optimistic);
      let resync = false;
      let pinnedOk = false;
      try {
        if (wasPinned) {
          await unpinConversation(id);
        } else {
          const at = await pinConversation(id);
          const ms = at ? Date.parse(at) : NaN;
          // El servidor manda el pinned_at real: con él se ordena igual que
          // tras un refetch. Solo se aplica si el id sigue fijado.
          if (Number.isFinite(ms) && pinsRef.current.has(id)) {
            commitPins(new Map(pinsRef.current).set(id, ms));
          }
          pinnedOk = true;
        }
      } catch (err) {
        // Rollback SOLO de este id, sobre el estado vigente: otro toggle pudo
        // haber cambiado otro id mientras esta petición estaba en vuelo.
        const rolled = new Map(pinsRef.current);
        if (wasPinned) rolled.set(id, previousAt ?? 0);
        else rolled.delete(id);
        commitPins(rolled);
        if (!wasPinned && isPinLimitError(err)) {
          toast.warning(t("Puedes fijar hasta 5 chats"));
          // Otra pestaña o dispositivo fijó: se resincroniza con el servidor.
          resync = true;
        } else {
          toast.error(
            apiErrorMessage(
              err,
              wasPinned ? t("No se pudo desfijar el chat.") : t("No se pudo fijar el chat.")
            )
          );
        }
      } finally {
        pinInFlightRef.current.delete(id);
        // También al TERMINAR: un GET que arrancó mientras el PATCH estaba en
        // vuelo trae la foto previa y no puede pisar el resultado.
        pinsVersionRef.current += 1;
      }
      if (resync) fetchPinsRef.current?.();
      // Fijada que no está en la página cargada (abierta por búsqueda, ticket
      // o aviso): se relee la lista, que devuelve primero las fijadas que
      // calzan con la bandeja. Sin esto no subía hasta la próxima reconciliación.
      if (pinnedOk && !conversationsRef.current.some((c) => c.id === id)) {
        fetchListRef.current?.();
      }
      // Sin toast de éxito: la fila moviéndose ES la confirmación.
    },
    [t, commitPins]
  );

  // ── Cuentas por canal ─────────────────────────────────────────────────────
  // `accountsVersion` fuerza un refetch tras retirar o migrar un número (el
  // rail cambia: el número sale o pasa a "Números retirados").
  const [accountsVersion, setAccountsVersion] = useState(0);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    fetchChannelAccounts()
      .then((accounts) => {
        if (cancelled) return;
        setChannelAccounts(accounts);
        setChannelAccountsLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        // null = cuentas desconocidas: el rail muestra todos los canales en vez
        // de esconder uno vinculado por un fallo de red.
        setChannelAccounts(null);
        // Endpoint no desplegado: silencio absoluto.
        if (!isEndpointMissing(err)) {
          console.warn("Cuentas por canal no disponibles:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user, accountsVersion]);

  // Cuenta retirada de la vista activa (para el aviso y el título).
  const retiredAccount = useMemo(
    () =>
      retiredAccountId && Array.isArray(channelAccounts)
        ? channelAccounts.find((a) => String(a?.id) === retiredAccountId) || null
        : null,
    [retiredAccountId, channelAccounts]
  );

  // El número retirado dejó de listarse (se migró, o se cerró y ya no tiene
  // conversaciones): la vista vuelve a la bandeja general.
  useEffect(() => {
    if (!retiredAccountId || !channelAccountsLoaded || !Array.isArray(channelAccounts)) return;
    if (!retiredAccount) setInboxKey("inbox.all");
  }, [retiredAccountId, retiredAccount, channelAccounts, channelAccountsLoaded]);

  // ── Búsqueda server-side (mensajes y ticket) ──────────────────────────────
  const {
    messageResults,
    messageSearchLoading,
    messageSearchUnavailable,
    messageSearchError,
    ticketResult,
    ticketLookupLoading,
    ticketLookupError,
    ticketLookupNumber,
  } = useInboxServerSearch(searchQuery);

  // Refs para reconciliar desde los handlers/eventos del SSE sin re-suscribir el
  // stream en cada cambio de filtro (el efecto del SSE depende solo de [user]).
  const fetchListRef = useRef(fetchList);
  useEffect(() => {
    fetchListRef.current = fetchList;
  }, [fetchList]);

  // Recarga el detalle de la conversación abierta desde Mongo (fuente de verdad).
  // Se usa al reconciliar tras una (re)conexión del SSE para recuperar mensajes
  // que hayan llegado durante el corte a la conversación que el agente tiene
  // abierta (el badge de no-leídos solo cubre las cerradas).
  const reloadOpenConversation = useCallback(async () => {
    const conv = selectedConvRef.current;
    if (!conv?.id) return;
    // La recarga no abre una selección nueva (no incrementa el contador): solo
    // vale si al responder sigue vigente la misma petición y la misma
    // conversación. Si en el medio el agente eligió otra, volvió o cambió de
    // bandeja, pisaría lo que tiene en pantalla con A. El id cubre además el
    // takeover de IA, que cambia la selección sin pasar por loadDetail.
    const requestId = detailRequestRef.current;
    const isStale = () =>
      requestId !== detailRequestRef.current || selectedConvRef.current?.id !== conv.id;
    try {
      const detail = await getConversation(conv.id, user, conv.channel);
      if (isStale()) return;
      setSelectedConv(detail);
      setDetailMessages(detail.messages ?? []);
      // Llegó el detalle: el fallo anterior (si lo hubo) quedó resuelto.
      setDetailError(null);
    } catch {
      /* noop: la lista ya se reconcilió; el detalle se recarga al reabrir */
    }
  }, [user]);
  const reloadOpenRef = useRef(reloadOpenConversation);
  useEffect(() => {
    reloadOpenRef.current = reloadOpenConversation;
  }, [reloadOpenConversation]);

  // ── Filtrado cliente-side (rail sub-view + búsqueda + commerce) ─────────
  // Las extras son solo el respaldo de un listado SIN overlay de fijados: si
  // la página trae `pinned`, el servidor ya puso las fijadas que corresponden
  // a esta bandeja (con filtros que el cliente no puede replicar: números de
  // notificaciones, retirados, ventana de 30 días de la IA) y una extra
  // guardada de otra bandeja no puede colarse.
  const conversationsWithPinned = useMemo(
    () =>
      listOverlaysPins
        ? conversations
        : withPinnedExtras(conversations, pinnedExtras, pins, baseFilters),
    [listOverlaysPins, conversations, pinnedExtras, pins, baseFilters]
  );
  const visibleConversations = useMemo(
    () =>
      sortPinnedFirst(
        filterVisibleConversations(conversationsWithPinned, {
          clientFilter,
          accountFilter,
          commerceFilter,
          searchQuery,
          getDisplayName,
          inboxKey,
          t,
        }).filter(
          // Etiqueta también en el cliente: saca de la unión las filas de portal
          // y logs de IA (no tienen etiquetas) y las que un status_change del SSE
          // dejó sin la etiqueta filtrada.
          (c) => !labelFilter || (Array.isArray(c.labels) && c.labels.includes(labelFilter))
        ),
        pins
      ),
    // Las fijadas suben DESPUÉS del filtrado y del orden de triage (Sin
    // asignar / SLA ordenan por antigüedad): dentro de cada bloque se respeta
    // ese orden.
    [conversationsWithPinned, pins, clientFilter, accountFilter, commerceFilter, labelFilter, searchQuery, getDisplayName, inboxKey, t]
  );

  // Cuenta (número) de la conversación abierta, tal como la trae la FILA de la
  // lista: el detalle (GET /:id) no la incluye, y el editor la necesita para
  // avisar por qué número saldrá la respuesta.
  const selectedChannelAccount = useMemo(() => {
    if (!selectedConv) return null;
    const row = conversations.find((c) => c.id === selectedConv.id);
    return row?.channel_account || selectedConv.channel_account || null;
  }, [selectedConv, conversations]);

  // ── Contadores del rail (calculados sobre la lista cargada) ────────────
  const counters = useMemo(
    () => computeInboxCounters(conversations, user?.id),
    [conversations, user?.id]
  );

  // ── Cargar detalle ────────────────────────────────────────────────────────
  // Sin layout: la página que lo consume decide si además cambia de pantalla
  // (móvil), en el mismo batch síncrono, antes del primer await.
  const loadDetail = useCallback(
    async (conv) => {
      setSelectedConv(conv);
      setDetailMessages([]);
      setDetailLoading(true);
      setDetailError(null);
      const requestId = ++detailRequestRef.current;
      setNewIds((prev) => {
        const next = new Set(prev);
        next.delete(conv.id);
        return next;
      });

      // Una selección abandonada no puede escribir NADA del detalle (ni los
      // mensajes, ni el error, ni el flag de carga): si su fetch resuelve
      // después del de la conversación que el agente abrió en el medio, pisaría
      // la selección vigente con datos de otra conversación.
      const isStale = () => requestId !== detailRequestRef.current;
      try {
        if (conv.channel === "ai") {
          const log = await getAILog(conv.id);
          if (isStale()) return;
          const messages = (log.messages ?? []).map((m, i) => ({
            id: `${conv.id}-msg-${i}`,
            conversation_id: conv.id,
            sender_role: m.role === "assistant" ? "ai" : m.role,
            sender_id: m.role === "user" ? log.user_id : null,
            content: m.content,
            created_at: log.created_at,
          }));
          setSelectedConv({
            ...conv,
            package_id: log.package_id,
            was_transferred: log.was_transferred,
            conversation_id: log.conversation_id || conv.conversation_id || null,
          });
          setDetailMessages(messages);
        } else {
          const detail = await getConversation(conv.id, user, conv.channel);
          if (isStale()) return;
          setSelectedConv(detail);
          setDetailMessages(detail.messages ?? []);
        }
      } catch (err) {
        console.error("Error al cargar detalle:", err);
        if (!isStale()) {
          setDetailError(apiErrorMessage(err, t("No se pudo cargar la conversación.")));
        }
      } finally {
        // El flag de carga pertenece a la petición vigente: la obsoleta no lo
        // apaga (dejaría el spinner apagado mientras B todavía está cargando).
        if (!isStale()) setDetailLoading(false);
      }
    },
    [user, t]
  );

  // Abrir una conversación por referencia (resultado de búsqueda en mensajes,
  // ticket consultado, origen de una incidencia): si no está en la lista
  // cargada, basta con id + canal, porque loadDetail trae el resto. Sin canal
  // se enruta como 'whatsapp': el canal solo decide si se va al proxy del
  // portal (ninguno o 'portal') o a omnicanal (cualquier otro), y el detalle
  // reemplaza la referencia por el documento real con su canal verdadero.
  /**
   * @param {{id: string, channel?: string|null, contact_name?: string,
   *   external_id?: string, contact_phone?: string}} ref
   * @returns {Promise<void>}
   */
  const openConversation = useCallback(
    (ref) => {
      const known = conversations.find((c) => c.id === ref.id);
      return loadDetail(known || { ...ref, channel: ref.channel ?? "whatsapp" });
    },
    [conversations, loadDetail]
  );

  // Abandonar la selección invalida el detalle en vuelo: si respondiera
  // después, reabriría la conversación que el agente acaba de dejar. Como la
  // petición invalidada ya no apaga su flag de carga, se apaga acá.
  const invalidateDetailRequest = useCallback(() => {
    detailRequestRef.current += 1;
    setDetailLoading(false);
  }, []);

  // Parte de datos de "volver a la lista": sin conversación abierta ni error.
  const clearSelection = useCallback(() => {
    invalidateDetailRequest();
    setSelectedConv(null);
    setDetailError(null);
  }, [invalidateDetailRequest]);

  const handleConvUpdated = useCallback((updated) => {
    setConversations((prev) => {
      const exists = prev.some((c) => c.id === updated.id);
      if (exists) {
        return prev.map((c) => (c.id === updated.id ? updated : c));
      }
      // Reapertura de caso: el backend reabre el MISMO doc y emite
      // status_change (nunca new_conversation, que es el único evento con
      // prepend). Si la conversación no está en la página cargada — resuelta
      // hace días, fuera de las 50 por last_message_at — sin este insert el
      // caso reabierto no aparecía en la bandeja hasta un reconcile. Las
      // resueltas no se insertan (nada pendiente que atender) y se respeta el
      // filtro de canal vigente; el clientFilter del render decide el resto
      // (carpeta/estado), igual que para cualquier otra fila.
      if (updated.status === "resolved") return prev;
      if (!channelMatchesFilter(updated.channel, channelFilterRef.current)) {
        return prev;
      }
      // La vista de un número retirado no recibe hilos de otros números (el
      // evento no dice de qué cuenta es; la lista se reconcilia con el fetch).
      if (retiredViewRef.current) return prev;
      return [updated, ...prev];
    });
    // Si la conversación es una fijada traída aparte, el payload reemplaza su
    // fila igual que en la lista (sin el hilo: el detalle no vive en la lista).
    setPinnedExtras((prev) => {
      if (!prev.has(updated.id)) return prev;
      const next = new Map(prev);
      const row = { ...updated };
      delete row.messages;
      next.set(updated.id, row);
      return next;
    });
    if (selectedConvRef.current?.id === updated.id) {
      setSelectedConv((previous) => keepDetailAgenda(previous, updated));
    }
  }, []);

  // Takeover de una atención IA: el bridge devuelve una conversación NUEVA
  // (channel portal, assigned) cuyo id difiere del id del log. handleConvUpdated
  // casa por id y no cambiaría la selección; por eso acá se selecciona explícito
  // y se inserta en la bandeja (si el filtro de canal la admite).
  const handleAiTakeover = useCallback(
    (newConv) => {
      handleConvUpdated(newConv);
      setSelectedConv(newConv);
      // Otra conversación: el error de carga de la anterior ya no aplica.
      setDetailError(null);
    },
    [handleConvUpdated],
  );

  // ── SSE helpdesk ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // Mensaje nuevo al estado de la bandeja (hilo abierto o marca de no leído).
    // Lo comparten new_message y notifications_inbound.
    const applyNewMessage = (msg) => {
      const activeId = selectedConvRef.current?.id;
      if (msg.conversation_id === activeId) {
        setDetailMessages((prev) => {
          const exists = prev.some((m) => m.id === msg.id);
          return exists ? prev : [...prev, msg];
        });
      } else if (msg.sender_role !== "system") {
        // Los mensajes de sistema (p.ej. audit trail de asignación) no son
        // actividad del contacto: no marcan la conversación como no leída.
        setNewIds((prev) => new Set(prev).add(msg.conversation_id));
      }
    };

    const handlers = {
      onNewConversation: (conv) => {
        if (!channelMatchesFilter(conv.channel, channelFilterRef.current)) return;
        // Un número retirado no recibe conversaciones nuevas: lo que llega es
        // de otro número y no pertenece a esta vista.
        if (retiredViewRef.current) return;
        setConversations((prev) => {
          const exists = prev.some((c) => c.id === conv.id);
          if (exists) return prev;
          return [conv, ...prev];
        });
        setNewIds((prev) => new Set(prev).add(conv.id));
      },

      onNewMessage: (msg) => {
        applyNewMessage(msg);
        // Solo el new_message propio avisa: lo que entra por un número de
        // notificaciones (onNotificationsInbound) nunca genera aviso.
        notifyDesktopRef.current?.(msg);
      },

      // Estado de entrega (enviado/entregado/leído/fallido). El evento todavía
      // no se emite: mientras no llegue, los mensajes quedan sin delivery_status
      // y el detalle no muestra ticks.
      onMessageStatus: (evt) => {
        const messageId = evt?.message_id ?? evt?.id;
        const status = evt?.delivery_status ?? evt?.status;
        if (!messageId || !status) return;
        setDetailMessages((prev) => {
          if (!prev.some((m) => m.id === messageId)) return prev;
          return prev.map((m) =>
            m.id === messageId ? { ...m, delivery_status: status } : m
          );
        });
      },

      // Respuesta de un contacto en un número dedicado a notificaciones: llega
      // en un evento propio porque las bandejas que no incluyen esos números
      // no deben verla. Sin "ver todas" se ignora; con él, la conversación se
      // actualiza o se inserta con las mismas reglas que una reapertura, y el
      // mensaje sigue el camino de new_message (sin aviso de escritorio).
      onNotificationsInbound: (evt) => {
        if (!showAllNumbersRef.current) return;
        const conv = evt?.conversation;
        if (!conv?.id) return;
        // La respuesta abrió un caso nuevo en la conversación abierta: el
        // separador del caso lo trae el detalle, no el evento.
        const open = selectedConvRef.current;
        const caseChanged =
          open?.id === conv.id && (conv.case_number || 1) !== (open.case_number || 1);
        handleConvUpdated(conv);
        if (evt.message?.id) applyNewMessage(evt.message);
        if (caseChanged) reloadOpenRef.current?.();
      },

      onAssignmentChange: (conv) => handleConvUpdated(conv),

      onStatusChange: (conv) => {
        handleConvUpdated(conv);
        if (conv.id === selectedConvRef.current?.id && conv.status === "resolved") {
          // Mismo merge que handleConvUpdated: sin él, esta segunda escritura
          // volvía a dejar la selección sin agenda.
          setSelectedConv((previous) => keepDetailAgenda(previous, conv));
        }
        // Los counts de carpeta excluyen resueltas: cada resolve/reapertura
        // los mueve. Debounce ~2s para no bombardear en ráfagas de SSE.
        if (folderRefetchTimerRef.current) clearTimeout(folderRefetchTimerRef.current);
        folderRefetchTimerRef.current = setTimeout(() => {
          folderRefetchTimerRef.current = null;
          fetchFoldersRef.current?.();
        }, 2000);
      },

      onReconnect: () => {
        // Al (re)conectar el SSE, resincroniza con la fuente de verdad (Mongo)
        // para recuperar lo emitido durante el corte: el pub/sub es volátil y no
        // tiene replay. Reconcilia la lista y, si hay una conversación abierta,
        // su detalle.
        fetchListRef.current?.();
        reloadOpenRef.current?.();
        fetchFoldersRef.current?.();
        // Los fijados son por usuario y no tienen evento SSE: al reconectar se
        // resincronizan.
        fetchPinsRef.current?.();
      },

      onError: (err) => {
        console.warn("SSE helpdesk error:", err);
      },
    };

    sseCleanupRef.current?.();
    // Sin fuentes legacy no se abre el stream del portal: con el bridge de IA
    // apagado quedaría reintentando un 503 en backoff mientras dure la vista.
    const cleanupAi = legacySources ? connectHelpdeskStream(user, handlers) : null;
    const cleanupOmni = connectOmniHelpdeskStream(user, handlers);
    sseCleanupRef.current = () => {
      cleanupAi?.();
      cleanupOmni?.();
    };

    // Red restaurada o pestaña vuelta a foco: reconciliar por si el SSE murió en
    // silencio (p.ej. el equipo despertó de suspensión) y aún no reconectó. Es
    // idempotente (un refetch de más nunca hace daño).
    const reconcile = () => {
      fetchListRef.current?.();
      reloadOpenRef.current?.();
      fetchFoldersRef.current?.();
      fetchPinsRef.current?.();
    };
    const onOnline = () => reconcile();
    const onVisible = () => {
      if (document.visibilityState === "visible") reconcile();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      sseCleanupRef.current?.();
      if (folderRefetchTimerRef.current) {
        clearTimeout(folderRefetchTimerRef.current);
        folderRefetchTimerRef.current = null;
      }
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Navegación de bandeja ─────────────────────────────────────────────────
  // Elegir una bandeja del rail (clave, cuenta del sub-árbol, número retirado o
  // carpeta) deja la vista sin conversación abierta, también la que estaba
  // cargando. Con cuenta, además filtra por ella; accountKeyOf(sin cuenta) es
  // null, que quita el filtro.
  const selectInbox = useCallback(
    (key, account) => {
      invalidateDetailRequest();
      setInboxKey(key);
      setSelectedConv(null);
      setAccountFilter(accountKeyOf(account));
    },
    [invalidateDetailRequest]
  );

  // Se retiró o migró un número (rail o aviso de la vista): cambian las
  // cuentas y la bandeja (lo migrado entra, lo retirado sale).
  const handleAccountsChanged = useCallback(() => {
    setAccountsVersion((v) => v + 1);
    fetchListRef.current?.();
    fetchFoldersRef.current?.();
  }, []);

  // ── Avisos de escritorio ──────────────────────────────────────────────────
  // El hook decide (preferencia, permiso, motivo) y avisa; acá solo se le da
  // acceso a la fila cargada (grupo, canal, nombre) y a cómo abrir el hilo.
  const conversationsRef = useRef(conversations);
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  const openConversationRef = useRef(openConversation);
  useEffect(() => {
    openConversationRef.current = openConversation;
  }, [openConversation]);
  const getActiveConvId = useCallback(() => selectedConvRef.current?.id ?? null, []);
  const getConvRow = useCallback(
    (id) => conversationsRef.current.find((c) => c.id === id) ?? null,
    []
  );
  const openFromNotification = useCallback((ref) => {
    openConversationRef.current?.(ref);
  }, []);
  const notifyDesktop = useDesktopNotifications({
    userId: user?.id,
    getActiveId: getActiveConvId,
    getRow: getConvRow,
    onOpen: openFromNotification,
    getDisplayName,
  });
  const notifyDesktopRef = useRef(notifyDesktop);
  useEffect(() => {
    notifyDesktopRef.current = notifyDesktop;
  }, [notifyDesktop]);

  // ── Handlers de carpetas ──────────────────────────────────────────────────
  // El CRUD rethrowea: el editor inline del rail es quien muestra los 409
  // (nombre duplicado / tope de 30). Tras cada mutación se refresca la lista
  // de carpetas (counts incluidos) vía ref, para que estos callbacks tengan
  // deps primitivas estables.
  const handleCreateFolder = useCallback(async (name, color) => {
    await createFolder({ name, color });
    fetchFoldersRef.current?.();
  }, []);

  const handleRenameFolder = useCallback(async (fid, name, color) => {
    await updateFolder(fid, { name, color });
    fetchFoldersRef.current?.();
  }, []);

  const handleDeleteFolder = useCallback(async (fid) => {
    const result = await deleteFolder(fid);
    // El backend desasigna (folder_id=null), no borra conversaciones. Se
    // refresca también la lista para que los chips de carpeta huérfanos
    // desaparezcan; si la carpeta era la vista activa, el efecto de arriba
    // devuelve la bandeja a inbox.all al llegar la lista sin ella.
    fetchFoldersRef.current?.();
    fetchListRef.current?.();
    return result;
  }, []);

  const handleMoveToFolder = useCallback(
    async (conv, fid) => {
      const updated = await setConversationFolder(conv.id, fid);
      // Idempotente con el SSE que emite el backend: si el evento llega antes
      // o después, handleConvUpdated pisa con el mismo estado final.
      if (updated?.id) handleConvUpdated(updated);
      fetchFoldersRef.current?.();
    },
    [handleConvUpdated]
  );

  // ── Derivados de la búsqueda server-side ──────────────────────────────────
  const trimmedQuery = searchQuery.trim();
  const showMessageSearch =
    trimmedQuery.length >= SEARCH_MIN_CHARS && !messageSearchUnavailable;
  // Número de ticket consultado, o null si la consulta no lo es.
  const ticketQueryNumber = parseTicketQuery(trimmedQuery);
  // Mismo aislamiento que la búsqueda de mensajes: un ticket del canal interno
  // (asistente de gerencia) no debe aparecer en una bandeja de mensajería.
  const ticketLookupVisible =
    ticketQueryNumber != null &&
    !(inboxKey !== "channel.internal" && ticketResult?.channel === "internal");
  // Lo que hay en el estado es del ticket ANTERIOR hasta que corre el efecto:
  // mientras el número no coincida se pinta el esqueleto, nunca el resultado de
  // otra consulta (afirmaría un identificador y un estado que no son los escritos).
  const ticketLookupFresh = ticketLookupNumber === ticketQueryNumber;
  const ticketLookupFound = ticketLookupVisible && !!ticketResult;

  // La búsqueda global de mensajes es otra fuente (endpoint aparte) y sus
  // resultados NO pasan por clientFilter: el aislamiento del canal interno hay
  // que aplicarlo acá. Sin esto, una consulta al asistente de gerencia
  // aparecería en la búsqueda de CUALQUIER bandeja (y al hacer clic abriría un
  // hilo que esa bandeja no debe mostrar).
  const visibleMessageResults = useMemo(
    () =>
      inboxKey === "channel.internal"
        ? messageResults
        : messageResults.filter(
            (r) => searchResultConversation(r).channel !== "internal"
          ),
    [messageResults, inboxKey]
  );

  // A6 · Búsqueda sin ningún resultado: la lista mostraba "Sin resultados /
  // Ningún caso coincide" y, cientos de píxeles más abajo, la sección "En los
  // mensajes" repetía "Ningún mensaje contiene ese texto". Dos vacíos
  // encadenados para un solo hecho. Cuando fallan las dos búsquedas, la
  // sección de mensajes no se monta y el vacío de la lista habla por ambas
  // (ver `messageSearchAlsoEmpty` en ConversationList).
  // La fila del ticket es una TERCERA fuente: si encontró algo (o lo está
  // buscando), el vacío de la lista no puede hablar por todas — diría "ningún
  // caso coincide" justo debajo del ticket que sí existe.
  const ticketLookupBusy =
    ticketLookupVisible && (!ticketLookupFresh || ticketLookupLoading || ticketLookupFound);
  const bothSearchesEmpty =
    showMessageSearch &&
    !ticketLookupBusy &&
    visibleConversations.length === 0 &&
    !listLoading &&
    !messageSearchLoading &&
    !messageSearchError &&
    visibleMessageResults.length === 0;

  return {
    // Filtros
    inboxKey,
    setInboxKey,
    selectInbox,
    statusFilter,
    channelFilter,
    folderId,
    aiState,
    retiredAccountId,
    activeFolderId,
    accountFilter,
    commerceFilter,
    setCommerceFilter,
    labelFilter,
    setLabelFilter,
    showAllNumbers,
    setShowAllNumbers,
    searchQuery,
    setSearchQuery,

    // Lista
    conversations,
    visibleConversations,
    listLoading,
    listError,
    aiLogsError,
    newIds,
    counters,
    refreshList: fetchList,

    // Detalle
    selectedConversation: selectedConv,
    detailMessages,
    detailLoading,
    detailError,
    selectedChannelAccount,
    selectConversation: loadDetail,
    openConversation,
    clearSelection,
    applyConversationUpdate: handleConvUpdated,
    // Relee el detalle abierto sin spinner (tras una macro, p. ej.).
    reloadConversation: reloadOpenConversation,
    applyTakeover: handleAiTakeover,

    // Nombres
    userNames,
    getDisplayName,

    // Carpetas
    folders,
    foldersById,
    createFolder: handleCreateFolder,
    renameFolder: handleRenameFolder,
    deleteFolder: handleDeleteFolder,
    moveToFolder: handleMoveToFolder,

    // Fijados
    pins,
    pinsAvailable,
    togglePin,

    // Cuentas
    channelAccounts,
    channelAccountsLoaded,
    retiredAccount,
    notifyAccountsChanged: handleAccountsChanged,

    // Búsqueda server-side
    trimmedQuery,
    showMessageSearch,
    messageSearchLoading,
    messageSearchError,
    visibleMessageResults,
    ticketQueryNumber,
    ticketLookupVisible,
    ticketLookupFresh,
    ticketLookupLoading,
    ticketResult,
    ticketLookupError,
    bothSearchesEmpty,
  };
}
